/** @forge-header
 * @id          kalshiApiOrders
 * @role        Order lifecycle | place, decrease, cancel + queue position enrichment
 * @pattern     adapter
 * @purity      api
 * @units       cents
 * @money-path  yes
 * @upstream    kalshiApi/client:authenticatedFetch | kalshiApi/types:KalshiOrder
 * @downstream  kalshiApi/index (via createKalshiClient)
 * @exports     placeOrder:fn | decreaseOrder:fn | cancelOrder:fn | cancelAllOrders:fn | getOpenOrders:fn
 * @test        untested (integration-level)
 *
 * @invariant   price-normalization | back-compat: 0..1 probability auto-converts to cents
 * @danger      placeOrder mutates exchange state | real money at risk
 */

import type { KalshiOrder } from './types';
import { authenticatedFetch } from './client';

/**
 * Get open orders
 */
export async function getOpenOrders(): Promise<KalshiOrder[]> {
  try {
    const response = await authenticatedFetch('GET', '/trade-api/v2/portfolio/orders', {
      status: 'resting',
    });
    const data = await response.json();

    // Handle different response formats
    let orders: KalshiOrder[] = [];
    if (Array.isArray(data)) {
      orders = data as KalshiOrder[];
    } else if (data.orders && Array.isArray(data.orders)) {
      orders = data.orders as KalshiOrder[];
    } else if (data.results && Array.isArray(data.results)) {
      orders = data.results as KalshiOrder[];
    } else {
      orders = [];
    }

    // Queue position is not reliably included on the list endpoint; fetch it via the dedicated endpoint.
    // Docs: GET /trade-api/v2/portfolio/orders/queue_positions
    // Note: Kalshi requires a filter (market_tickers or event_ticker) for this endpoint.
    // Skip entirely when there are no orders — avoids 400 "Need to specify market_tickers" error.
    if (orders.length === 0) return orders;
    try {
      const uniqueTickers = Array.from(
        new Set(orders.map((o) => String(o.ticker ?? '')).filter((t) => !!t))
      );
      const qpQuery: Record<string, string> =
        uniqueTickers.length > 0 ? { market_tickers: uniqueTickers.join(',') } : {};
      const qpResp = await authenticatedFetch(
        'GET',
        '/trade-api/v2/portfolio/orders/queue_positions',
        qpQuery
      );
      const qpData = (await qpResp.json()) as unknown;
      const qpObj = qpData as Record<string, unknown> | null;
      const qpArr = (qpObj && Array.isArray(qpObj.queue_positions) && qpObj.queue_positions) || [];

      const byId = new Map<string, number>();
      for (const rec of qpArr) {
        if (!rec || typeof rec !== 'object') continue;
        const recObj = rec as Record<string, unknown>;
        const oid = String(recObj.order_id ?? recObj.orderId ?? '');
        const qp = Number(recObj.queue_position ?? recObj.queuePosition);
        if (!oid) continue;
        if (!Number.isFinite(qp)) continue;
        byId.set(oid, qp);
      }

      if (byId.size > 0) {
        orders = orders.map((o) => ({
          ...o,
          queue_position: byId.get(o.order_id) ?? o.queue_position,
        }));
      } else if (orders.length > 0) {
        // Fallback: per-order endpoint (works even when bulk endpoint is empty).
        // Docs: GET /trade-api/v2/portfolio/orders/{order_id}/queue_position
        const perOrder = await Promise.all(
          orders.map(async (o) => {
            try {
              const r = await authenticatedFetch(
                'GET',
                `/trade-api/v2/portfolio/orders/${o.order_id}/queue_position`,
                {}
              );
              const j = (await r.json()) as Record<string, unknown> | null;
              const qp = Number(j?.queue_position ?? j?.queuePosition);
              return { orderId: o.order_id, qp: Number.isFinite(qp) ? qp : undefined };
            } catch {
              return { orderId: o.order_id, qp: undefined };
            }
          })
        );
        const fallbackMap = new Map<string, number>();
        for (const row of perOrder) {
          if (row.qp === undefined) continue;
          fallbackMap.set(row.orderId, row.qp);
        }
        if (fallbackMap.size > 0) {
          orders = orders.map((o) => ({
            ...o,
            queue_position: fallbackMap.get(o.order_id) ?? o.queue_position,
          }));
        }
      }
    } catch (e) {
      // Non-fatal: we can still show open orders without queue positions.
      console.warn('Failed to fetch queue positions:', e);
    }

    return orders;
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    // Don't silently swallow auth errors — callers (e.g. OrderMonitor) need to see them
    // so they can stop polling and surface a clear message to the user.
    if (
      message.includes('API error: 401') ||
      message.includes('API error: 403') ||
      message.includes('"code":"authentication_error"') ||
      message.includes('"code": "authentication_error"')
    ) {
      throw error;
    }
    console.warn('Failed to fetch open orders:', error);
    return [];
  }
}

/**
 * Place an order
 */
export async function placeOrder(order: {
  ticker: string;
  side: 'yes' | 'no';
  action: 'buy' | 'sell';
  type: 'limit' | 'market';
  count: number;
  price?: number;
  /** If true, the order must not cross the book (maker / post-only). */
  post_only?: boolean;
  /** Kalshi time-in-force: fill_or_kill | good_till_canceled | immediate_or_cancel */
  time_in_force?: 'fill_or_kill' | 'good_till_canceled' | 'immediate_or_cancel';
  /** Expiration time in UTC epoch seconds (only meaningful with GTC). */
  expiration_ts?: number;
  expires_at?: string;
}): Promise<KalshiOrder> {
  const priceField = order.side === 'yes' ? 'yes_price' : 'no_price';
  const normalizePriceToCents = (raw: number): number => {
    // Back-compat: some callers still pass 0..1 probability.
    if (raw <= 1.01) return Math.round(raw * 100);
    return Math.round(raw);
  };
  const body = {
    ticker: order.ticker,
    side: order.side,
    action: order.action,
    type: order.type,
    count: order.count,
    ...(order.price !== undefined && { [priceField]: normalizePriceToCents(order.price) }),
    ...(order.post_only !== undefined && { post_only: order.post_only }),
    ...(order.time_in_force && { time_in_force: order.time_in_force }),
    ...(order.expiration_ts !== undefined && { expiration_ts: order.expiration_ts }),
    ...(order.expires_at && { expires_at: order.expires_at }),
  };

  const response = await authenticatedFetch('POST', '/trade-api/v2/portfolio/orders', {}, body);
  const data = await response.json();

  if (data.order) {
    return data.order;
  }
  return data as KalshiOrder;
}

/**
 * Decrease an order's remaining quantity without repricing.
 */
export async function decreaseOrder(
  orderId: string,
  args: {
    reduce_by?: number;
    reduce_to?: number;
    client_order_id?: string;
    cancel_order_on_pause?: boolean;
  }
): Promise<KalshiOrder> {
  const body: Record<string, unknown> = {};
  if (args.reduce_by !== undefined) body.reduce_by = args.reduce_by;
  if (args.reduce_to !== undefined) body.reduce_to = args.reduce_to;
  if (args.client_order_id !== undefined) body.client_order_id = args.client_order_id;
  if (args.cancel_order_on_pause !== undefined)
    body.cancel_order_on_pause = args.cancel_order_on_pause;

  const response = await authenticatedFetch(
    'POST',
    `/trade-api/v2/portfolio/orders/${orderId}/decrease`,
    {},
    body
  );
  const data = await response.json();
  if (data.order) {
    return data.order as KalshiOrder;
  }
  return data as KalshiOrder;
}

/**
 * Cancel an order
 */
export async function cancelOrder(orderId: string): Promise<void> {
  await authenticatedFetch('DELETE', `/trade-api/v2/portfolio/orders/${orderId}`);
}

/**
 * Cancel all open orders
 */
export async function cancelAllOrders(): Promise<void> {
  // First get all open orders
  const orders = await getOpenOrders();

  // Cancel each one
  const cancelPromises = orders.map((order) => cancelOrder(order.order_id));
  await Promise.all(cancelPromises);
}
